import React, {useEffect, useState} from "react";
import ModalComponent from "../../../../../components/modals/ModalFormComponent";
import Label from "../../../../../components/form_fields/Label";
import OrangeButton from "../../../../../components/buttons/OrangeButton";
import {useCreateOrganizationRole, useGetOrganizationRole} from "../../../../../services/organization-role.service";
import {PermissionDto} from "../../../../../models/permission.dto";
import useUserStore from "../../../../../state-management/useUserStore";
import {SubmitHandler, useForm} from "react-hook-form";

type IFormInputs = {
    name: string;
    active: boolean;
};

type INewOrganizationRole = {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    permissionsData: PermissionDto[];
    addNewData: () => void;
};


const NewOrganizationRole = ({
                                 open,
                                 setOpen,
                                 permissionsData,
                                 addNewData,
                             }: INewOrganizationRole) => {
    const userOrganization = useUserStore().currentUser.user.organization;
    const {mutate: createRole} = useCreateOrganizationRole();
    const [uploading, setUploading] = useState(false);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [permissionsError, setPermissionsError] = useState(false);
    const [roles, setRoles] = useState<{ id: string, name: string, permissions?: string[] }[]>([]);
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');

    // Fetch organization roles
    const {data: organizationRoles} = useGetOrganizationRole({
        organizationId: userOrganization?.id,
        limit: 20, // Adjust as needed
    });

    useEffect(() => {
        if (organizationRoles?.data) {
            const formattedRoles = organizationRoles.data.map(role => ({
                id: role.id,
                name: role.name,
                permissions: role.permissions || [], // Ensure permissions is always an array
            }));

            console.log("Formatted Roles:", formattedRoles);
            setRoles(formattedRoles);
        }
    }, [organizationRoles]);

    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm<IFormInputs>();

    const updatePermissions = (newEntry: string) => {
        const optionAlreadyPresent = permissions.find((permission) => {
            return permission === newEntry;
        });
        const filteredPermissions = permissions.filter((permission) => {
            return permission !== newEntry;
        });

        if (optionAlreadyPresent) {
            setPermissions(filteredPermissions);
        } else {
            setPermissions(prev => [...prev, newEntry]);
        }
    };

    // Set default permissions when permission data is loaded
    useEffect(() => {
        if (permissionsData.length < 1) return;

        // Only set default permissions if no role is selected
        if (!selectedRoleId) {
            const defaultPermissions = permissionsData
                .filter(permission => permission.isDefault)
                .map(permission => permission.code);

            setPermissions(defaultPermissions);
        }
    }, [permissionsData, selectedRoleId]);

    // Fetch role details when a role is selected
    useEffect(() => {
        if (!selectedRoleId) return;

        const selectedRole = roles.find(role => role.id === selectedRoleId);

        if (selectedRole) {
            // Set permissions based on selected role
            setPermissions(selectedRole.permissions || []);
        }
    }, [selectedRoleId, roles]);

    const onSubmit: SubmitHandler<IFormInputs> = (data) => {
        if (!permissions.length) {
            setPermissionsError(true);
            return;
        }
        setUploading(true);

        createRole(
            {
                payload: {
                    organizationId: userOrganization?.id as string,
                    permissions,
                    ...data,
                },
            },
            {
                onSuccess: () => {
                    addNewData();
                    setOpen(false);
                },
                onSettled: () => setUploading(false),
            }
        );
    };

    const handleClose = () => setOpen(false);

    return (
        <ModalComponent
            width={850}
            open={open}
            title={"Add New Role"}
            handleClose={handleClose}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid w-full grid-cols-200 gap-x-4 gap-y-6">
                    <div className="flex gap-6">
                        <div className="w-1/2">
                            <Label title="Name" for="name"/>
                            <input
                                placeholder="Enter name"
                                type="text"
                                {...register("name", {
                                    required: "Enter name here",
                                })}
                                id="name"
                                className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
                                    errors.name
                                        ? "border border-red-500 focus:border-red-500 focus:outline-red-500"
                                        : "border-none"
                                }`}
                            />
                            {errors.name && (
                                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                            )}
                        </div>

                        <div className="w-1/2">
                            <Label title="Existing role" for="role"/>
                            <select
                                id="role"
                                className="w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm text-gray-500 border-none focus:ring-2 focus:ring-primary focus:outline-none"
                                value={selectedRoleId}
                                onChange={(e) => setSelectedRoleId(e.target.value)}
                            >
                                <option value="" className="text-gray-400">
                                    Select an existing role
                                </option>
                                {roles.map((role) => (
                                    <option key={role.id} value={role.id}>
                                        {role.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="w-full col-span-2 flex space-x-3 items-center">
                        <input
                            type="checkbox"
                            {...register("active", {
                                required: false,
                            })}
                            id="active"
                            defaultChecked={true}
                        />
                        <label htmlFor="active">Active</label>
                    </div>
                </div>
                <div>
                    <p className="text-gray-500 text-sm mt-8">
                        Select permissions for this role
                    </p>
                    <ul className="mt-4 space-y-6">
                        {permissionsData.map((permission) => (
                            <li key={permission.code} className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    name={permission.code}
                                    id={permission.code}
                                    checked={permissions.includes(permission.code)}
                                    onChange={() => updatePermissions(permission.code)}
                                />
                                <label htmlFor={permission.code}>{permission.name}</label>
                            </li>
                        ))}
                    </ul>
                    {permissionsError && (
                        <p className="text-red-500 text-sm mt-4">
                            Select at least one permission to continue
                        </p>
                    )}
                </div>

                <div className="flex justify-end mt-3">
                    <OrangeButton
                        title={uploading ? "Uploading..." : "Create Role"}
                        disabled={uploading}
                        className={"h-12 px-24"}
                        type="submit"
                    />
                </div>
            </form>
        </ModalComponent>
    );
};

export default NewOrganizationRole;
